import { useEffect } from 'react';
import {
  Stack,
  Title,
  Paper,
  Table,
  Loader,
  Center,
  Text,
  Pagination,
  Select,
  Group,
  Badge,
  SegmentedControl,
} from '@mantine/core';
import type { Detection } from '../types';
import { DetectionDetailDrawer } from '../components/DetectionDetailDrawer';
import { useStore } from '../store/useStore';

export function Detections() {
  const {
    detections,
    detectionsLoading: loading,
    detectionsPage: page,
    detectionsTotalCount: total,
    detectionsPageSize: pageSize,
    detectionsProcessingState: processingState,
    selectedDetection,
    detectionDrawerOpened,
    setDetectionsPage,
    setDetectionsPageSize,
    setDetectionsProcessingState,
    fetchDetections,
    openDetectionDrawer,
    closeDetectionDrawer,
  } = useStore();

  useEffect(() => {
    fetchDetections();
  }, [page, pageSize, processingState, fetchDetections]);

  const handleProcessingStateChange = (value: string) => {
    setDetectionsProcessingState(value as 'pending' | 'processing' | 'completed' | 'failed' | 'all');
    setDetectionsPage(1);
  };

  const handleViewDetection = (detection: Detection) => {
    openDetectionDrawer(detection);
  };

  const handlePageSizeChange = (value: string | null) => {
    if (value) {
      setDetectionsPageSize(parseInt(value));
      setDetectionsPage(1);
    }
  };

  const getDetectionType = (detection: Detection): string => {
    if (detection.plate) {
      return 'License Plate';
    }
    if (detection.faces.length > 0) {
      return 'Face';
    }
    return 'Wireless Only';
  };

  const getProcessingStateColor = (state: string) => {
    switch (state) {
      case 'completed':
        return 'green';
      case 'processing':
        return 'blue';
      case 'pending':
        return 'yellow';
      case 'failed':
        return 'red';
      default:
        return 'gray';
    }
  };

  return (
    <>
      <Stack gap="lg">
        <Group justify="space-between" align="center">
          <Title order={1}>Detections</Title>
          <Badge size="lg" color="gray" variant="light">
            {total} total
          </Badge>
        </Group>

        <Paper withBorder p="md">
          <Group gap="md">
            <Text size="sm" fw={500}>Status:</Text>
            <SegmentedControl
              value={processingState}
              onChange={handleProcessingStateChange}
              data={[
                { label: 'All', value: 'all' },
                { label: 'Completed', value: 'completed' },
                { label: 'Processing', value: 'processing' },
                { label: 'Pending', value: 'pending' },
                { label: 'Failed', value: 'failed' },
              ]}
            />
          </Group>
        </Paper>

        {loading ? (
          <Center h={400}>
            <Loader size="lg" />
          </Center>
        ) : detections.length > 0 ? (
          <>
            <Paper withBorder p="md">
              <Table striped highlightOnHover>
                <Table.Thead>
                  <Table.Tr>
                    <Table.Th>Timestamp</Table.Th>
                    <Table.Th>Status</Table.Th>
                    <Table.Th>Type</Table.Th>
                    <Table.Th>Location</Table.Th>
                    <Table.Th>Facility</Table.Th>
                    <Table.Th>Sensor</Table.Th>
                  </Table.Tr>
                </Table.Thead>
                <Table.Tbody>
                  {detections.map((detection) => (
                    <Table.Tr
                      key={detection.observation_id}
                      onClick={() => handleViewDetection(detection)}
                      style={{ cursor: 'pointer' }}
                    >
                      <Table.Td>
                        <Text size="sm">
                          {new Date(detection.timestamp).toLocaleString()}
                        </Text>
                      </Table.Td>
                      <Table.Td>
                        <Badge
                          size="sm"
                          color={getProcessingStateColor(detection.processing_state || 'completed')}
                        >
                          {detection.processing_state || 'completed'}
                        </Badge>
                      </Table.Td>
                      <Table.Td>
                        <Text size="sm">{getDetectionType(detection)}</Text>
                      </Table.Td>
                      <Table.Td>
                        <Text size="sm" c="dimmed">
                          {detection.location
                            ? `${detection.location.lat.toFixed(5)}, ${detection.location.lon.toFixed(5)}`
                            : 'N/A'}
                        </Text>
                      </Table.Td>
                      <Table.Td>
                        <Text size="sm">{detection.facility_id}</Text>
                      </Table.Td>
                      <Table.Td>
                        <Text size="sm" c="dimmed">
                          {detection.sensor_id}
                        </Text>
                      </Table.Td>
                    </Table.Tr>
                  ))}
                </Table.Tbody>
              </Table>
            </Paper>

            <Group justify="space-between" mt="md">
              <Pagination
                total={Math.ceil(total / pageSize)}
                value={page}
                onChange={setDetectionsPage}
              />
              <Group gap="xs">
                <Text size="sm">Per page:</Text>
                <Select
                  value={pageSize.toString()}
                  onChange={handlePageSizeChange}
                  data={['10', '20', '50', '100']}
                  w={80}
                />
              </Group>
            </Group>
          </>
        ) : (
          <Center h={400}>
            <Text c="dimmed">No detections found</Text>
          </Center>
        )}
      </Stack>

      <DetectionDetailDrawer
        detection={selectedDetection}
        opened={detectionDrawerOpened}
        onClose={closeDetectionDrawer}
      />
    </>
  );
}
